import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';

import type { components } from '../shared/api/openapi';
import type { BaseQuery } from './baseQuery.types';

type GetOrganizationsResponse = components['schemas']['GetOrganizationsEndpoint_Response'];

export interface UseOrganizationsReturn extends BaseQuery {
  organizations: GetOrganizationsResponse[] | null;
}

export function useOrganizations(): UseOrganizationsReturn {
  const { getAccessToken } = useAuth();

  const getOrganizations = async () => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations');

    return Array.isArray(data) ? (data as GetOrganizationsResponse[]) : [];
  };

  const query = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      try {
        return await getOrganizations();
      } catch (err) {
        if (err instanceof Error) {
          throw err;
        }
        throw new Error(err ? String(err) : 'Unknown error occurred');
      }
    },
    retry: false,
  });

  const { data, isLoading, error, refetch, isRefetching } = query;

  return {
    organizations: data ?? null,
    isLoading,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
